import {FC, PropsWithChildren, useEffect, useMemo} from 'react'
import {ThemeProvider} from 'styled-components'

import {ThemeType} from '../types'
import {brandFontOf, brandThemeColors} from './brand-theme'
import {BrandFont, brandFontCssUrl} from './branding'

// Loads one brand face's Google Fonts stylesheet, once, only where a venue page is painted.
const useBrandFont = (font: BrandFont | null) => {
  useEffect(() => {
    if (!font) {
      return
    }
    const id = `brand-font-${font}`
    if (document.getElementById(id)) {
      return
    }
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = brandFontCssUrl(font)
    document.head.appendChild(link)
  }, [font])
}

type Props = PropsWithChildren<{venue?: {brandColors?: unknown; brandFont?: unknown} | null}>

/**
 * A venue's brand over the app theme, for the pages under one venue. Nests inside the app's ThemeProvider and
 * overrides only the colours the venue set; with none set it renders its children untouched. The modals the
 * ModalProvider mounts above the router sit outside this and keep the app colours.
 */
export const VenueBrandTheme: FC<Props> = ({venue, children}) => {
  const overrides = useMemo(() => brandThemeColors(venue), [venue])
  useBrandFont(brandFontOf(venue))
  if (Object.keys(overrides).length === 0) {
    return <>{children}</>
  }
  const theme = (outer: ThemeType): ThemeType => ({...outer, colors: {...outer.colors, ...overrides}})
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>
}
